import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Receipt, Store, Upload, CheckCircle2, XCircle, AlertTriangle, Camera,
  Plus, X, ChevronDown, ChevronRight, Inbox, FolderOpen,
  DollarSign, Clock, Check, Ban, RefreshCw,
} from 'lucide-react'
import { api } from '../lib/api'

const C = {
  bg: 'var(--color-bg)', bgRaised: 'var(--color-bg-card)', bgCard: '#1A2212',
  border: 'var(--color-border)', amber: 'var(--color-primary)', green: '#2D6A2F',
  text: 'var(--color-text)', textMuted: 'var(--color-text-muted)', textFaint: 'var(--color-text-faint)',
  red: '#F87171', blue: '#60A5FA', teal: '#2DD4BF',
}

const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box',
  background: C.bg, border: `1px solid ${C.border}`,
  borderRadius: 8, padding: '8px 12px',
  color: C.text, fontSize: 13, outline: 'none',
}
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 500, color: C.textMuted, marginBottom: 5,
}
const btnPrimary: React.CSSProperties = {
  background: C.amber, color: '#1A1200', border: 'none', borderRadius: 8,
  padding: '8px 18px', fontWeight: 700, fontSize: 13, cursor: 'pointer',
}
const btnSecondary: React.CSSProperties = {
  background: 'transparent', color: C.textMuted,
  border: `1px solid ${C.border}`, borderRadius: 8,
  padding: '8px 14px', fontSize: 13, cursor: 'pointer',
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface Vendor {
  id: string; name: string; vendor_type: string; contact_name?: string
  contact_email?: string; contact_phone?: string; notes: string
  is_tax_exempt: boolean; generate_1099: boolean; ytd_payments_usd: number
  is_active: boolean; tax_id?: string
}

interface ReceiptExtraction {
  id: string; receipt_upload_id: string
  vendor_name?: string; receipt_date?: string
  total_amount?: number; suggested_category?: string
  confidence_score?: number; review_status: string
  original_filename?: string; signed_read_url?: string
  override_vendor_name?: string; override_amount?: number
  override_category?: string; override_date?: string
  payment_method?: string; tax_amount?: number
  line_items?: Array<{ description: string; total_price: number }>
}

interface QueueSummary {
  pending: number; needs_review: number; total_amount: number
}

interface DriveIntegration {
  id?: string
  access_method: 'sa_sharing' | 'custom_sa'
  drive_folder_id: string
  service_account_email: string
  service_account_key_ref?: string  // null for sa_sharing
  gcs_staging_prefix: string
  is_active: boolean
  last_synced_at?: string
  last_sync_file_count?: number
  last_error?: string
}

const GRAFTY_SA_EMAIL = 'grafty-platform@grafty-platform.iam.gserviceaccount.com'

// ── Shared helpers ────────────────────────────────────────────────────────────

const ACCT_CATEGORIES = [
  { value: 'cogs', label: 'Cost of Goods Sold' },
  { value: 'supplies', label: 'Supplies' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'labor', label: 'Labor' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'fees', label: 'Fees & Licenses' },
  { value: 'tax', label: 'Taxes' },
  { value: 'other', label: 'Other' },
]

function confidenceBadge(score?: number) {
  if (score == null) return null
  const pct = Math.round(score * 100)
  const color = score >= 0.8 ? '#4ADE80' : score >= 0.6 ? C.amber : C.red
  return (
    <span style={{ fontSize: 11, fontWeight: 700, color, background: `${color}1A`, borderRadius: 6, padding: '2px 7px' }}>
      {pct}% conf.
    </span>
  )
}

function fmtUSD(n?: number) {
  if (n == null) return '—'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n)
}

function fmtDate(s?: string) {
  if (!s) return '—'
  const d = new Date(s)
  return isNaN(d.getTime()) ? s : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// ── Add Vendor Modal ──────────────────────────────────────────────────────────

function AddVendorModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const [form, setForm] = useState({
    name: '', vendor_type: 'supplier', contact_name: '', contact_email: '',
    contact_phone: '', notes: '', is_tax_exempt: false, generate_1099: false,
    is_active: true, country: 'US',
  })
  const [saved, setSaved] = useState(false)
  const mut = useMutation({
    mutationFn: (d: Record<string, unknown>) => api.post('/accounting/vendors', d),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['acct-vendors'] })
      setSaved(true)
      setTimeout(onClose, 1200)
    },
  })
  const set = (k: string, v: string | boolean) => setForm(f => ({ ...f, [k]: v }))
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background: C.bgRaised, border: `1px solid ${C.border}`, borderRadius: 14, width: 480, maxWidth: '95vw', padding: 24, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>Add Expense Vendor</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textFaint }}><X size={18} /></button>
        </div>
        {saved ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: '#4ADE80' }}>
            <CheckCircle2 size={36} style={{ marginBottom: 10 }} />
            <div style={{ fontSize: 15, fontWeight: 600 }}>Vendor added!</div>
          </div>
        ) : (
          <form onSubmit={e => { e.preventDefault(); mut.mutate({ ...form }) }} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={labelStyle}>Vendor name *</label>
              <input style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)} required placeholder="e.g. Farmstead Supply Co." />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={labelStyle}>Type</label>
                <select style={inputStyle} value={form.vendor_type} onChange={e => set('vendor_type', e.target.value)}>
                  {['supplier','contractor','utility','government','financial','retailer','other'].map(t => (
                    <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Contact name</label>
                <input style={inputStyle} value={form.contact_name} onChange={e => set('contact_name', e.target.value)} placeholder="e.g. Jane Smith" />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={labelStyle}>Contact email</label>
                <input style={inputStyle} type="email" value={form.contact_email} onChange={e => set('contact_email', e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Contact phone</label>
                <input style={inputStyle} value={form.contact_phone} onChange={e => set('contact_phone', e.target.value)} />
              </div>
            </div>
            <div>
              <label style={labelStyle}>Notes</label>
              <textarea style={{ ...inputStyle, height: 70, resize: 'vertical' }} value={form.notes} onChange={e => set('notes', e.target.value)} />
            </div>
            {/* Accounting flags */}
            <div style={{ display: 'flex', gap: 20 }}>
              {[
                { key: 'is_tax_exempt', label: 'Tax exempt' },
                { key: 'generate_1099', label: 'Generate 1099-NEC' },
              ].map(({ key, label }) => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: C.textMuted }}>
                  <input type="checkbox" checked={form[key as keyof typeof form] as boolean}
                    onChange={e => set(key, e.target.checked)} />
                  {label}
                </label>
              ))}
            </div>
            {mut.isError && <p style={{ color: C.red, fontSize: 13, margin: 0 }}>Failed to save. Please try again.</p>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 6 }}>
              <button type="button" style={btnSecondary} onClick={onClose}>Cancel</button>
              <button type="submit" style={btnPrimary} disabled={mut.isPending}>
                {mut.isPending ? 'Saving…' : 'Add Vendor'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

// ── Approve Modal ─────────────────────────────────────────────────────────────

function ApproveModal({ extraction, vendors, onClose }: {
  extraction: ReceiptExtraction
  vendors: Vendor[]
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [form, setForm] = useState({
    vendor_id: '',
    vendor_name: extraction.override_vendor_name ?? extraction.vendor_name ?? '',
    create_vendor: false,
    category: extraction.override_category ?? extraction.suggested_category ?? 'other',
    date: extraction.override_date ?? extraction.receipt_date ?? new Date().toISOString().split('T')[0],
    amount: String(extraction.override_amount ?? extraction.total_amount ?? ''),
    is_tax_deductible: true,
    is_resale_goods: false,
    payment_method: extraction.payment_method ?? '',
    notes: '',
  })
  const set = (k: string, v: string | boolean) => setForm(f => ({ ...f, [k]: v }))
  const [saved, setSaved] = useState(false)
  const mut = useMutation({
    mutationFn: (d: Record<string, unknown>) =>
      api.post(`/accounting/queue/${extraction.id}/approve`, d),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['acct-queue'] })
      qc.invalidateQueries({ queryKey: ['acct-queue-summary'] })
      setSaved(true)
      setTimeout(onClose, 1000)
    },
  })
  function submit(e: React.FormEvent) {
    e.preventDefault()
    mut.mutate({
      vendor_id: form.vendor_id || undefined,
      vendor_name: form.vendor_name || undefined,
      create_vendor: form.create_vendor,
      category: form.category,
      date: form.date,
      amount: parseFloat(form.amount),
      is_tax_deductible: form.is_tax_deductible,
      is_resale_goods: form.is_resale_goods,
      payment_method: form.payment_method || undefined,
      notes: form.notes || undefined,
    })
  }
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background: C.bgRaised, border: `1px solid ${C.border}`, borderRadius: 14, width: 560, maxWidth: '95vw', padding: 24, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>Approve Receipt</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textFaint }}><X size={18} /></button>
        </div>
        <p style={{ margin: '0 0 18px', fontSize: 13, color: C.textMuted }}>{extraction.original_filename}</p>
        {saved ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: '#4ADE80' }}>
            <CheckCircle2 size={36} style={{ marginBottom: 10 }} />
            <div style={{ fontSize: 15, fontWeight: 600 }}>Approved!</div>
          </div>
        ) : (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Vendor selection */}
            <div>
              <label style={labelStyle}>Vendor</label>
              <select style={inputStyle} value={form.vendor_id}
                onChange={e => set('vendor_id', e.target.value)}>
                <option value="">— None / Create new —</option>
                {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
            {!form.vendor_id && (
              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Vendor name</label>
                  <input style={inputStyle} value={form.vendor_name} onChange={e => set('vendor_name', e.target.value)} placeholder="e.g. Ace Hardware" />
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: C.textMuted, paddingTop: 20, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.create_vendor} onChange={e => set('create_vendor', e.target.checked)} />
                  Save as vendor
                </label>
              </div>
            )}
            {/* Category + date + amount */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={labelStyle}>Category *</label>
                <select style={inputStyle} value={form.category} onChange={e => set('category', e.target.value)} required>
                  {ACCT_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Date *</label>
                <input type="date" style={inputStyle} value={form.date} onChange={e => set('date', e.target.value)} required />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={labelStyle}>Amount ($) *</label>
                <input type="number" min="0.01" step="0.01" style={inputStyle} value={form.amount} onChange={e => set('amount', e.target.value)} required />
              </div>
              <div>
                <label style={labelStyle}>Payment method</label>
                <select style={inputStyle} value={form.payment_method} onChange={e => set('payment_method', e.target.value)}>
                  <option value="">— Unknown —</option>
                  {['cash','check','credit_card','ach','wire','paypal','venmo','other'].map(m => (
                    <option key={m} value={m}>{m.replace('_', ' ')}</option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 20 }}>
              {[
                { key: 'is_tax_deductible', label: 'Tax deductible' },
                { key: 'is_resale_goods', label: 'Resale goods (COGS)' },
              ].map(({ key, label }) => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: C.textMuted }}>
                  <input type="checkbox" checked={form[key as keyof typeof form] as boolean}
                    onChange={e => set(key, e.target.checked)} />
                  {label}
                </label>
              ))}
            </div>
            <div>
              <label style={labelStyle}>Notes</label>
              <input style={inputStyle} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Optional notes for this expense" />
            </div>
            {mut.isError && <p style={{ color: C.red, fontSize: 13, margin: 0 }}>Approval failed. Please try again.</p>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 6 }}>
              <button type="button" style={btnSecondary} onClick={onClose}>Cancel</button>
              <button type="submit" style={{ ...btnPrimary, background: '#4ADE80', color: '#0D1F0D' }} disabled={mut.isPending}>
                {mut.isPending ? 'Approving…' : '✓ Approve & Create Expense'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

// ── Tab: Vendor Registry ──────────────────────────────────────────────────────

function VendorRegistryTab() {
  const [showAdd, setShowAdd] = useState(false)
  const [expanded, setExpanded] = useState<string | null>(null)
  const { data: vendors = [], isLoading } = useQuery<Vendor[]>({
    queryKey: ['acct-vendors'],
    queryFn: () => api.get('/accounting/vendors').then(r => r.data),
  })
  const qc = useQueryClient()
  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(`/accounting/vendors/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['acct-vendors'] }),
  })

  const totalYTD = vendors.reduce((s, v) => s + v.ytd_payments_usd, 0)
  const need1099 = vendors.filter(v => v.generate_1099 && v.ytd_payments_usd >= 600).length

  return (
    <div>
      {/* Stats */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { label: 'Total vendors', value: vendors.length, color: C.blue },
          { label: 'YTD payments', value: fmtUSD(totalYTD), color: C.amber },
          { label: '1099 required', value: need1099, color: need1099 > 0 ? C.amber : C.textFaint },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: '14px 20px', minWidth: 150 }}>
            <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
            <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <span style={{ fontSize: 13, color: C.textMuted }}>{vendors.length} vendor{vendors.length !== 1 ? 's' : ''}</span>
        <button style={btnPrimary} onClick={() => setShowAdd(true)}>
          <Plus size={14} style={{ verticalAlign: 'middle', marginRight: 5 }} />
          Add Vendor
        </button>
      </div>

      {isLoading && <div style={{ color: C.textMuted, fontSize: 14 }}>Loading…</div>}

      {!isLoading && vendors.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px 0', color: C.textFaint }}>
          <Store size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
          <div style={{ fontSize: 15, fontWeight: 600 }}>No vendors yet</div>
          <div style={{ fontSize: 13, marginTop: 6 }}>Add vendors to track which suppliers you buy from.</div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {vendors.map(v => (
          <div key={v.id} style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden' }}>
            <button
              onClick={() => setExpanded(expanded === v.id ? null : v.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '13px 16px', textAlign: 'left' }}
            >
              {expanded === v.id ? <ChevronDown size={14} color={C.textFaint} /> : <ChevronRight size={14} color={C.textFaint} />}
              <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: C.text }}>{v.name}</span>
              <span style={{ fontSize: 12, color: C.textMuted, background: `${C.border}`, borderRadius: 6, padding: '2px 8px' }}>{v.vendor_type}</span>
              {v.generate_1099 && v.ytd_payments_usd >= 600 && (
                <span style={{ fontSize: 11, color: C.amber, background: `${C.amber}1A`, borderRadius: 6, padding: '2px 7px' }}>1099</span>
              )}
              <span style={{ fontSize: 14, fontWeight: 600, color: C.amber, minWidth: 80, textAlign: 'right' }}>{fmtUSD(v.ytd_payments_usd)}</span>
            </button>
            {expanded === v.id && (
              <div style={{ padding: '0 16px 16px', borderTop: `1px solid ${C.border}` }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10, marginTop: 14 }}>
                  {v.contact_name && <div><div style={{ ...labelStyle, marginBottom: 2 }}>Contact</div><div style={{ fontSize: 13, color: C.text }}>{v.contact_name}</div></div>}
                  {v.contact_email && <div><div style={{ ...labelStyle, marginBottom: 2 }}>Email</div><div style={{ fontSize: 13, color: C.text }}>{v.contact_email}</div></div>}
                  {v.contact_phone && <div><div style={{ ...labelStyle, marginBottom: 2 }}>Phone</div><div style={{ fontSize: 13, color: C.text }}>{v.contact_phone}</div></div>}
                  <div><div style={{ ...labelStyle, marginBottom: 2 }}>Tax exempt</div><div style={{ fontSize: 13, color: C.text }}>{v.is_tax_exempt ? 'Yes' : 'No'}</div></div>
                  <div><div style={{ ...labelStyle, marginBottom: 2 }}>YTD payments</div><div style={{ fontSize: 13, color: C.amber, fontWeight: 700 }}>{fmtUSD(v.ytd_payments_usd)}</div></div>
                </div>
                {v.notes && <p style={{ fontSize: 13, color: C.textMuted, margin: '10px 0 0' }}>{v.notes}</p>}
                <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                  <button
                    onClick={() => { if (window.confirm(`Delete vendor "${v.name}"?`)) deleteMut.mutate(v.id) }}
                    style={{ ...btnSecondary, color: C.red, borderColor: `${C.red}40`, fontSize: 12 }}>
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {showAdd && <AddVendorModal onClose={() => setShowAdd(false)} />}
    </div>
  )
}

// ── Tab: Receipt Queue ────────────────────────────────────────────────────────

function ReceiptQueueTab() {
  const qc = useQueryClient()
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [approveTarget, setApproveTarget] = useState<ReceiptExtraction | null>(null)
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [batchBusy, setBatchBusy] = useState(false)

  const { data: summary } = useQuery<QueueSummary>({
    queryKey: ['acct-queue-summary'],
    queryFn: () => api.get('/accounting/queue/summary').then(r => r.data),
    refetchInterval: 30_000,
  })
  const { data: items = [], isLoading } = useQuery<ReceiptExtraction[]>({
    queryKey: ['acct-queue', statusFilter],
    queryFn: () => api.get(`/accounting/queue${statusFilter ? `?status=${statusFilter}` : ''}`).then(r => r.data),
    refetchInterval: 30_000,
  })
  const { data: vendors = [] } = useQuery<Vendor[]>({
    queryKey: ['acct-vendors'],
    queryFn: () => api.get('/accounting/vendors').then(r => r.data),
  })

  const toggleSelect = (id: string) => setSelected(s => {
    const n = new Set(s)
    if (n.has(id)) { n.delete(id) } else { n.add(id) }
    return n
  })
  const selectAll = () => setSelected(new Set(items.filter(i => i.review_status === 'pending' || i.review_status === 'needs_review').map(i => i.id)))
  const clearSelection = () => setSelected(new Set())

  async function batchApprove() {
    if (selected.size === 0) return
    setBatchBusy(true)
    try {
      await api.post('/accounting/queue/batch-approve', { ids: Array.from(selected) })
      qc.invalidateQueries({ queryKey: ['acct-queue'] })
      qc.invalidateQueries({ queryKey: ['acct-queue-summary'] })
      setSelected(new Set())
    } finally {
      setBatchBusy(false)
    }
  }

  const statusColor: Record<string, string> = {
    pending: C.amber, needs_review: C.red, approved: '#4ADE80', rejected: C.textFaint,
  }
  const statusIcon: Record<string, React.ReactNode> = {
    pending: <Clock size={12} />,
    needs_review: <AlertTriangle size={12} />,
    approved: <Check size={12} />,
    rejected: <Ban size={12} />,
  }

  return (
    <div>
      {/* Summary chips */}
      {summary && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: '14px 20px', minWidth: 140 }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: C.amber }}>{summary.pending}</div>
            <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>Pending review</div>
          </div>
          <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: '14px 20px', minWidth: 140 }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: C.red }}>{summary.needs_review}</div>
            <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>Low confidence</div>
          </div>
          <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 12, padding: '14px 20px', minWidth: 160 }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: C.text }}>{fmtUSD(summary.total_amount)}</div>
            <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>Total in queue</div>
          </div>
        </div>
      )}

      {/* Batch actions bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <select style={{ ...inputStyle, width: 'auto' }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="needs_review">Needs review</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        <button style={btnSecondary} onClick={selectAll}>Select all</button>
        {selected.size > 0 && (
          <>
            <button style={btnSecondary} onClick={clearSelection}>Clear ({selected.size})</button>
            <button
              style={{ ...btnPrimary, background: '#4ADE80', color: '#0D1F0D' }}
              onClick={batchApprove} disabled={batchBusy}>
              {batchBusy ? <RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={13} style={{ marginRight: 5 }} />}
              Approve {selected.size} selected
            </button>
          </>
        )}
        <button style={btnSecondary} onClick={() => { qc.invalidateQueries({ queryKey: ['acct-queue'] }); qc.invalidateQueries({ queryKey: ['acct-queue-summary'] }) }}>
          <RefreshCw size={13} style={{ marginRight: 4 }} />Refresh
        </button>
      </div>

      {isLoading && <div style={{ color: C.textMuted, fontSize: 14 }}>Loading…</div>}

      {!isLoading && items.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px 0', color: C.textFaint }}>
          <Inbox size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
          <div style={{ fontSize: 15, fontWeight: 600 }}>Queue is empty</div>
          <div style={{ fontSize: 13, marginTop: 6 }}>Receipts will appear here after scanning or Drive sync.</div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map(item => {
          const isSel = selected.has(item.id)
          const isReviewable = item.review_status === 'pending' || item.review_status === 'needs_review'
          return (
            <div key={item.id} style={{
              background: C.bgCard, border: `1px solid ${isSel ? C.amber : C.border}`,
              borderRadius: 10, padding: '14px 16px',
              display: 'flex', alignItems: 'flex-start', gap: 14,
            }}>
              {isReviewable && (
                <input type="checkbox" checked={isSel} onChange={() => toggleSelect(item.id)}
                  style={{ marginTop: 3, accentColor: C.amber, cursor: 'pointer' }} />
              )}
              {/* Receipt thumbnail — opens the file via the authenticated proxy */}
              {item.signed_read_url ? (
                <button type="button" onClick={() => openReceiptFile(item.receipt_upload_id)}
                  title="View receipt"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 56, background: C.bgRaised, borderRadius: 8, border: `1px solid ${C.border}`, flexShrink: 0, color: C.textFaint, cursor: 'pointer' }}>
                  <Receipt size={20} />
                </button>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 56, background: C.bgRaised, borderRadius: 8, border: `1px solid ${C.border}`, flexShrink: 0, color: C.textFaint }}>
                  <Receipt size={20} />
                </div>
              )}
              {/* Main info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>
                    {item.vendor_name ?? item.override_vendor_name ?? item.original_filename ?? 'Unknown vendor'}
                  </span>
                  <span style={{ fontSize: 11, color: statusColor[item.review_status] ?? C.textMuted, background: `${statusColor[item.review_status] ?? C.textMuted}1A`, borderRadius: 6, padding: '2px 7px', display: 'flex', alignItems: 'center', gap: 4 }}>
                    {statusIcon[item.review_status]}{item.review_status.replace('_', ' ')}
                  </span>
                  {confidenceBadge(item.confidence_score)}
                </div>
                <div style={{ display: 'flex', gap: 16, marginTop: 6, flexWrap: 'wrap' }}>
                  {item.receipt_date && <span style={{ fontSize: 12, color: C.textMuted }}>{fmtDate(item.receipt_date)}</span>}
                  {item.suggested_category && <span style={{ fontSize: 12, color: C.teal }}>{ACCT_CATEGORIES.find(c => c.value === item.suggested_category)?.label ?? item.suggested_category}</span>}
                  {item.total_amount != null && <span style={{ fontSize: 13, fontWeight: 700, color: C.amber }}>{fmtUSD(item.total_amount)}</span>}
                </div>
                {item.line_items && item.line_items.length > 0 && (
                  <div style={{ marginTop: 6, fontSize: 11, color: C.textFaint }}>
                    {item.line_items.slice(0, 3).map((li, i) => (
                      <span key={i} style={{ marginRight: 10 }}>{li.description} {fmtUSD(li.total_price)}</span>
                    ))}
                    {item.line_items.length > 3 && <span>+{item.line_items.length - 3} more</span>}
                  </div>
                )}
              </div>
              {/* Action buttons */}
              {isReviewable && (
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <button
                    onClick={() => setApproveTarget(item)}
                    style={{ ...btnPrimary, padding: '6px 14px', fontSize: 12, background: '#4ADE80', color: '#0D1F0D' }}>
                    <Check size={12} style={{ marginRight: 4 }} />Review
                  </button>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {approveTarget && (
        <ApproveModal
          extraction={approveTarget}
          vendors={vendors}
          onClose={() => setApproveTarget(null)}
        />
      )}
    </div>
  )
}

// ── Tab: Upload Receipt ───────────────────────────────────────────────────────

// prepareReceiptFile downscales image receipts to fit the 2 MB API request limit
// (canvas → JPEG, quality-stepped). Non-images (PDF) pass through unchanged.
async function prepareReceiptFile(file: File): Promise<File> {
  if (!file.type.startsWith('image/')) return file
  const img = await new Promise<HTMLImageElement>((res, rej) => {
    const i = new Image()
    i.onload = () => res(i)
    i.onerror = rej
    i.src = URL.createObjectURL(file)
  })
  const maxDim = 2000
  let { width, height } = img
  if (width > maxDim || height > maxDim) {
    const scale = maxDim / Math.max(width, height)
    width = Math.round(width * scale)
    height = Math.round(height * scale)
  }
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  canvas.getContext('2d')!.drawImage(img, 0, 0, width, height)
  const toBlob = (q: number) => new Promise<Blob>((res) => canvas.toBlob((b) => res(b!), 'image/jpeg', q))
  let quality = 0.85
  let blob = await toBlob(quality)
  while (blob.size > 1.9 * 1024 * 1024 && quality > 0.4) {
    quality -= 0.15
    blob = await toBlob(quality)
  }
  return new File([blob], file.name.replace(/\.\w+$/, '') + '.jpg', { type: 'image/jpeg' })
}

// openReceiptFile streams the stored receipt through the authenticated proxy
// (objects are private — a plain <a href> can't carry the JWT) and opens it.
async function openReceiptFile(uploadId: string) {
  try {
    const resp = await api.get(`/accounting/receipts/${uploadId}/file`, { responseType: 'blob' })
    const url = URL.createObjectURL(resp.data as Blob)
    window.open(url, '_blank', 'noopener')
  } catch {
    /* ignore — thumbnail is best-effort */
  }
}

function UploadTab() {
  const qc = useQueryClient()
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [done, setDone] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function handleUpload() {
    if (!file) return
    setUploading(true)
    setErr(null)
    try {
      // Single multipart POST — the API stores the file in the tenant's bucket
      // (keyless) and triggers async Gemini extraction. Images are downscaled
      // client-side to fit the 2 MB request limit (same as photos/docimport).
      const prepared = await prepareReceiptFile(file)
      if (prepared.size > 2 * 1024 * 1024) {
        throw new Error('File is over 2 MB. Photos compress automatically; please use a smaller PDF.')
      }
      const fd = new FormData()
      fd.append('file', prepared, prepared.name)
      await api.post('/accounting/receipts', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      qc.invalidateQueries({ queryKey: ['acct-queue'] })
      qc.invalidateQueries({ queryKey: ['acct-queue-summary'] })
      setDone(true)
    } catch (e) {
      const ax = e as { response?: { data?: { error?: string } } }
      setErr(ax.response?.data?.error || (e instanceof Error ? e.message : 'Upload failed'))
    } finally {
      setUploading(false)
    }
  }

  return (
    <div style={{ maxWidth: 520 }}>
      <h3 style={{ margin: '0 0 8px', fontSize: 15, fontWeight: 700, color: C.text }}>Upload Receipt</h3>
      <p style={{ margin: '0 0 20px', fontSize: 13, color: C.textMuted }}>
        Upload a receipt photo or PDF. Gemini Vision will extract vendor, date, and amounts — then it lands in the review queue.
      </p>
      {done ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#4ADE80' }}>
          <CheckCircle2 size={40} style={{ marginBottom: 12 }} />
          <div style={{ fontSize: 15, fontWeight: 600 }}>Receipt uploaded!</div>
          <div style={{ fontSize: 13, color: C.textMuted, marginTop: 6 }}>Gemini Vision is extracting the details. Check the queue in a moment.</div>
          <button style={{ ...btnSecondary, marginTop: 20 }} onClick={() => { setDone(false); setFile(null) }}>Upload another</button>
        </div>
      ) : (
        <>
          {/* ── Mobile camera — primary capture on phones ── */}
          <label
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              padding: '14px 20px', borderRadius: 12, cursor: 'pointer',
              background: 'color-mix(in srgb, var(--color-primary) 12%, transparent)', border: `1px solid color-mix(in srgb, var(--color-primary) 30%, transparent)`,
              marginBottom: 10, transition: 'background 0.15s',
            }}
          >
            <Camera size={20} color={C.amber} />
            <span style={{ fontSize: 14, fontWeight: 600, color: C.amber }}>
              Snap receipt photo
            </span>
            <span style={{ fontSize: 12, color: C.textMuted, marginLeft: 4 }}>mobile camera</span>
            {/* capture="environment" opens rear camera on mobile devices */}
            <input
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={e => setFile(e.target.files?.[0] ?? null)}
            />
          </label>

          {/* ── Desktop file picker / drag-drop ── */}
          <label
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              gap: 10, border: `2px dashed ${file ? C.amber : C.border}`,
              borderRadius: 12, padding: '32px 20px', cursor: 'pointer',
              background: C.bgCard, transition: 'border-color 0.15s',
            }}
          >
            <Upload size={22} color={file ? C.amber : C.textFaint} />
            <span style={{ fontSize: 13, color: file ? C.text : C.textFaint, fontWeight: file ? 600 : 400, textAlign: 'center' }}>
              {file ? file.name : 'Or click to choose from files / drag & drop'}
            </span>
            {file && <span style={{ fontSize: 12, color: C.textMuted }}>{(file.size / 1024).toFixed(1)} KB · {file.type || 'file'}</span>}
            <span style={{ fontSize: 11, color: C.textFaint }}>JPG, PNG, HEIC (auto-compressed) or PDF up to 2 MB</span>
            <input type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={e => setFile(e.target.files?.[0] ?? null)} />
          </label>

          {/* ── Selected file preview ── */}
          {file && file.type.startsWith('image/') && (
            <div style={{ marginTop: 10, borderRadius: 10, overflow: 'hidden', border: `1px solid ${C.border}`, maxHeight: 200 }}>
              <img
                src={URL.createObjectURL(file)}
                alt="Receipt preview"
                style={{ width: '100%', objectFit: 'cover', maxHeight: 200 }}
              />
            </div>
          )}

          {err && <p style={{ color: C.red, fontSize: 13, marginTop: 10 }}>{err}</p>}
          <button
            style={{ ...btnPrimary, marginTop: 16, width: '100%', padding: '11px' }}
            onClick={handleUpload} disabled={!file || uploading}>
            {uploading ? 'Uploading…' : 'Upload & Scan'}
          </button>
        </>
      )}
    </div>
  )
}

// ── Tab: Drive Setup ──────────────────────────────────────────────────────────

function DriveSetupTab() {
  const qc = useQueryClient()
  const { data: cfg, isLoading } = useQuery<DriveIntegration | null>({
    queryKey: ['acct-drive'],
    queryFn: () => api.get('/accounting/drive').then(r => r.data).catch(() => null),
  })

  const [accessMethod, setAccessMethod] = useState<'sa_sharing' | 'custom_sa'>('sa_sharing')
  const [folderID, setFolderID] = useState('')
  const [customSAEmail, setCustomSAEmail] = useState('')
  const [customSAKeyRef, setCustomSAKeyRef] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [copied, setCopied] = useState(false)
  const [saved, setSaved] = useState(false)

  // Pre-fill from existing config
  const [prefilled, setPrefilled] = useState(false)
  if (cfg && !prefilled) {
    setAccessMethod(cfg.access_method ?? 'sa_sharing')
    setFolderID(cfg.drive_folder_id ?? '')
    setCustomSAEmail(cfg.access_method === 'custom_sa' ? cfg.service_account_email : '')
    setIsActive(cfg.is_active)
    setPrefilled(true)
  }

  const saveMut = useMutation({
    mutationFn: (d: Record<string, unknown>) => api.put('/accounting/drive', d),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['acct-drive'] })
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    },
  })
  const deleteMut = useMutation({
    mutationFn: () => api.delete('/accounting/drive'),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['acct-drive'] }); setPrefilled(false) },
  })

  function handleCopy() {
    navigator.clipboard.writeText(GRAFTY_SA_EMAIL).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000) })
  }

  function handleSave(e: React.FormEvent) {
    e.preventDefault()
    saveMut.mutate({
      access_method: accessMethod,
      drive_folder_id: folderID,
      service_account_email: accessMethod === 'custom_sa' ? customSAEmail : GRAFTY_SA_EMAIL,
      service_account_key_ref: accessMethod === 'custom_sa' ? customSAKeyRef : '',
      is_active: isActive,
    })
  }

  const hasConnection = !!cfg?.drive_folder_id

  return (
    <div style={{ maxWidth: 580 }}>
      <h3 style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 700, color: C.text }}>Google Drive Receipt Sync</h3>
      <p style={{ margin: '0 0 20px', fontSize: 13, color: C.textMuted, lineHeight: 1.6 }}>
        Connect a Google Drive folder. Grafty polls it every 5 minutes and auto-scans any new PDF or image receipts.
      </p>

      {/* Status card if already connected */}
      {hasConnection && (
        <div style={{
          background: C.bgCard, border: `1px solid ${C.border}`,
          borderRadius: 10, padding: '12px 16px', marginBottom: 20,
          display: 'flex', gap: 12, alignItems: 'flex-start',
        }}>
          <CheckCircle2 size={16} color="#4ADE80" style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>Drive connected</div>
            <div style={{ fontSize: 12, color: C.textMuted, marginTop: 3, fontFamily: 'monospace', wordBreak: 'break-all' }}>
              Folder: {cfg?.drive_folder_id}
            </div>
            {cfg?.last_synced_at && (
              <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>
                Last sync: {fmtDate(cfg.last_synced_at)} · {cfg.last_sync_file_count ?? 0} files ingested
              </div>
            )}
            {cfg?.last_error && (
              <div style={{ fontSize: 12, color: C.red, marginTop: 2 }}>{cfg.last_error}</div>
            )}
          </div>
        </div>
      )}

      {/* Access method selector */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>Access method</label>
        <div style={{ display: 'flex', gap: 10 }}>
          {([
            { val: 'sa_sharing' as const, label: 'Grafty Service Account', desc: 'Recommended — share your folder with Grafty\'s account. No keys to manage.' },
            { val: 'custom_sa' as const, label: 'Custom Service Account', desc: 'Advanced — bring your own GCP service account and JSON key.' },
          ]).map(({ val, label, desc }) => (
            <button
              key={val}
              type="button"
              onClick={() => setAccessMethod(val)}
              style={{
                flex: 1, padding: '10px 12px', borderRadius: 10, border: 'none', cursor: 'pointer', textAlign: 'left',
                background: accessMethod === val ? 'color-mix(in srgb, var(--color-primary) 12%, transparent)' : C.bgCard,
                outline: accessMethod === val ? `1.5px solid ${C.amber}` : `1px solid ${C.border}`,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 3 }}>{label}</div>
              <div style={{ fontSize: 11, color: C.textFaint, lineHeight: 1.4 }}>{desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* SA sharing — step-by-step instructions */}
      {accessMethod === 'sa_sharing' && (
        <div style={{
          background: C.bgCard, border: `1px solid ${C.border}`,
          borderRadius: 12, padding: '16px 18px', marginBottom: 20,
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.amber, marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Setup — 3 steps
          </div>
          {[
            {
              n: 1,
              title: 'Open Google Drive',
              body: (
                <span>
                  Go to <a href="https://drive.google.com" target="_blank" rel="noopener noreferrer"
                    style={{ color: C.amber }}>drive.google.com</a> and navigate to (or create) the folder where you drop receipt photos and PDFs.
                </span>
              ),
            },
            {
              n: 2,
              title: 'Share the folder with Grafty',
              body: (
                <span>
                  Right-click the folder → <strong style={{ color: C.text }}>Share</strong> → paste this email and grant <strong style={{ color: C.text }}>Viewer</strong> access:
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8, marginTop: 8,
                    background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8,
                    padding: '8px 12px',
                  }}>
                    <code style={{ flex: 1, fontSize: 12, color: C.amber, wordBreak: 'break-all' }}>{GRAFTY_SA_EMAIL}</code>
                    <button
                      type="button"
                      onClick={handleCopy}
                      style={{ flexShrink: 0, background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 11, color: C.textMuted }}
                    >
                      {copied ? '✓ Copied' : 'Copy'}
                    </button>
                  </div>
                </span>
              ),
            },
            {
              n: 3,
              title: 'Paste the folder ID below',
              body: (
                <span>
                  The folder ID is in the URL when you open the folder:
                  <span style={{ display: 'block', fontFamily: 'monospace', fontSize: 11, color: C.textFaint, marginTop: 4 }}>
                    drive.google.com/drive/folders/<strong style={{ color: C.amber }}>1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs</strong>
                  </span>
                </span>
              ),
            },
          ].map(({ n, title, body }) => (
            <div key={n} style={{ display: 'flex', gap: 12, marginBottom: n < 3 ? 14 : 0 }}>
              <div style={{
                width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                background: 'color-mix(in srgb, var(--color-primary) 15%, transparent)', border: `1.5px solid ${C.amber}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 700, color: C.amber,
              }}>{n}</div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 3 }}>{title}</div>
                <div style={{ fontSize: 12, color: C.textMuted, lineHeight: 1.5 }}>{body}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <label style={labelStyle}>Google Drive folder ID *</label>
          <input
            style={inputStyle}
            value={folderID}
            onChange={e => setFolderID(e.target.value)}
            required
            placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs"
          />
        </div>

        {/* Custom SA fields */}
        {accessMethod === 'custom_sa' && (
          <>
            <div>
              <label style={labelStyle}>Service account email *</label>
              <input style={inputStyle} value={customSAEmail} onChange={e => setCustomSAEmail(e.target.value)}
                required placeholder="receipts@your-project.iam.gserviceaccount.com" />
            </div>
            <div>
              <label style={labelStyle}>Secret Manager key path *</label>
              <input style={inputStyle} value={customSAKeyRef} onChange={e => setCustomSAKeyRef(e.target.value)}
                required={accessMethod === 'custom_sa'}
                placeholder="projects/123456/secrets/drive-sa-key/versions/latest" />
              <div style={{ fontSize: 11, color: C.textFaint, marginTop: 4 }}>
                Path to the SA JSON key in GCP Secret Manager.
              </div>
            </div>
          </>
        )}

        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13, color: C.textMuted }}>
          <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
          Integration active (polling every 5 minutes)
        </label>

        {saveMut.isError && (
          <p style={{ color: C.red, fontSize: 13, margin: 0 }}>Failed to save. Check required fields.</p>
        )}
        {saved && (
          <p style={{ color: '#4ADE80', fontSize: 13, margin: 0 }}>✓ Drive integration saved! First sync runs in the next 5 minutes.</p>
        )}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button type="submit" style={btnPrimary} disabled={saveMut.isPending || isLoading}>
            {saveMut.isPending ? 'Saving…' : hasConnection ? 'Update Integration' : 'Connect Drive'}
          </button>
          {hasConnection && (
            <button
              type="button"
              style={{ ...btnSecondary, color: C.red, borderColor: `${C.red}40` }}
              onClick={() => { if (window.confirm('Remove Drive integration? Grafty will stop syncing from this folder.')) deleteMut.mutate() }}
            >
              <XCircle size={13} style={{ marginRight: 4 }} />Disconnect
            </button>
          )}
        </div>
      </form>
    </div>
  )
}

// ── Main AccountingPage ───────────────────────────────────────────────────────

type Tab = 'queue' | 'upload' | 'vendors' | 'drive'

export default function AccountingPage() {
  const [tab, setTab] = useState<Tab>('queue')

  const { data: summary } = useQuery<QueueSummary>({
    queryKey: ['acct-queue-summary'],
    queryFn: () => api.get('/accounting/queue/summary').then(r => r.data),
    refetchInterval: 60_000,
  })

  const tabs: Array<{ id: Tab; label: string; icon: React.ReactNode; badge?: number }> = [
    { id: 'queue', label: 'Receipt Queue', icon: <Inbox size={15} />, badge: (summary?.pending ?? 0) + (summary?.needs_review ?? 0) },
    { id: 'upload', label: 'Upload Receipt', icon: <Upload size={15} /> },
    { id: 'vendors', label: 'Vendor Registry', icon: <Store size={15} /> },
    { id: 'drive', label: 'Drive Setup', icon: <FolderOpen size={15} /> },
  ]

  return (
    <main id="main-content" style={{ padding: '28px 32px', minHeight: '100%', boxSizing: 'border-box', background: C.bg }}>
      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 28 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: `${C.amber}1A`, border: `1px solid ${C.amber}40`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <DollarSign size={20} color={C.amber} />
        </div>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.text }}>Accounting</h1>
          <p style={{ margin: 0, fontSize: 13, color: C.textMuted, marginTop: 2 }}>
            Expense vendors · Receipt scanning · Finance approval queue
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 28, borderBottom: `1px solid ${C.border}`, paddingBottom: 0 }}>
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 7,
              padding: '8px 16px', border: 'none', cursor: 'pointer',
              background: 'transparent', fontSize: 13, fontWeight: tab === t.id ? 700 : 400,
              color: tab === t.id ? C.amber : C.textMuted,
              borderBottom: tab === t.id ? `2px solid ${C.amber}` : '2px solid transparent',
              marginBottom: -1, transition: 'color 0.1s',
            }}
          >
            {t.icon}
            {t.label}
            {t.badge != null && t.badge > 0 && (
              <span style={{ background: C.red, color: '#fff', borderRadius: 20, fontSize: 10, fontWeight: 700, padding: '1px 6px', marginLeft: 2 }}>
                {t.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'queue' && <ReceiptQueueTab />}
      {tab === 'upload' && <UploadTab />}
      {tab === 'vendors' && <VendorRegistryTab />}
      {tab === 'drive' && <DriveSetupTab />}
    </main>
  )
}
